import { useEffect, useMemo, useRef, useState } from "react"
import { calculateRowTopPosition, enrichDiscussionWithRelevance, isNewCommentBubble } from "./utils"
import { useDiscussions } from "@/services/store/useDiscussions"
import { Discussion as DiscussionI, DiscussionState } from "@/models/Discussions"
import { DiscussionsHeader } from "@/components/designSystem/Discussions/DiscussionHeader"
import ResolvedIcon from "@/assets/icons/Resolved.svg?react"
import OutdatedIcon from "@/assets/icons/Outdated.svg?react"
import YellowOpenIcon from "@/assets/icons/YellowOpen.svg?react"
import { NewCommentBubble as NewCommentBubbleElement } from "@/components/designSystem/Discussions/components/Comment/NewCommentBubble"
import { Discussion } from "./Discussion"
import { Commit } from "@/models/Changes"
import { usePermissions } from "@/services/store/usePermissions"

export const iconsMap: Record<DiscussionState, JSX.Element> = {
  resolved: <ResolvedIcon />,
  open: <YellowOpenIcon />,
  pending: <YellowOpenIcon />,
  outdated: <OutdatedIcon />
} as const

interface Props {
  crId: string
  crCommits: Commit[]
  discussionsToShow: DiscussionI[]
}

export const DiscussionsLayout = ({ crId, crCommits, discussionsToShow }: Props) => {
  const { discussionsExpanded, filteredDiscussions, setDiscussionsWithTop, toggleManualRecalculationTrigger } =
    useDiscussions()

  const commitSha = crCommits && crCommits[0]?.sha
  const { user } = usePermissions()
  const [domReady, setDomReady] = useState(false)
  const sectionRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const checkSection = () => {
      const section = document.querySelector("section[data-id='pr-content-section']")
      if (section) {
        sectionRef.current = section as HTMLElement
        setDomReady(true)
        return true
      }
      return false
    }

    if (domReady || checkSection()) return

    const pollInterval = setInterval(checkSection, 100)
    return () => clearInterval(pollInterval)
  }, [domReady])

  const processedDiscussions = useMemo(() => {
    const enrichedDiscussions = enrichDiscussionWithRelevance(discussionsToShow, user)
    return enrichedDiscussions.map((enrichedDisc) => ({
      ...enrichedDisc,
      topPosition:
        domReady && sectionRef.current
          ? calculateRowTopPosition(enrichedDisc)
          : enrichedDisc.start_line || enrichedDisc.end_line || 0
    }))
  }, [discussionsToShow, user, domReady])

  useEffect(() => {
    if (domReady && discussionsExpanded !== undefined) {
      setTimeout(() => {
        const updatedDiscussions = processedDiscussions.map((discussion) => ({
          ...discussion,

          topPosition: calculateRowTopPosition(discussion)
        }))

        setDiscussionsWithTop(updatedDiscussions)
        toggleManualRecalculationTrigger()
      }, 10)
    }
  }, [discussionsExpanded, domReady, processedDiscussions, setDiscussionsWithTop, toggleManualRecalculationTrigger])

  return (
    <div
      id="discussion-wrapper"
      className={`flex flex-col ${discussionsExpanded ? "discussion-width-container" : "min-w-closed-discussion"}`}
    >
      <DiscussionsHeader />

      <div id="generated-discussions" className="relative flex-1 p-2">
        {filteredDiscussions.map((discussion, index) => {
          const positionToUse = discussion.topPosition || discussion.start_line || 0

          return isNewCommentBubble(discussion) ? (
            <NewCommentBubbleElement
              discussions={filteredDiscussions}
              total={filteredDiscussions.length}
              key={discussion.id}
              discussionNumber={index}
              current={discussion}
              crId={crId}
              commitSha={commitSha}
              topPosition={positionToUse}
            />
          ) : (
            <Discussion
              total={filteredDiscussions.length}
              key={discussion.id}
              discussionNumber={index}
              current={discussion as DiscussionI}
              crId={crId}
              commitSha={commitSha}
              topPosition={positionToUse}
            />
          )
        })}
      </div>
    </div>
  )
}
